import { useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { normalizarNombre } from '@eventos-cien/shared';
import type { Invitacion } from '@eventos-cien/shared';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Chip } from '../components/Chip';
import { EstadoInvitacionBadge } from '../components/EstadoInvitacionBadge';
import { QR } from '../components/QR';
import { TextField } from '../components/TextField';
import { db } from '../lib/firebase';
import { useAdminEventoData, type MesaConId } from './AdminEventoDataProvider';

type Filtro = 'todos' | 'registrados' | 'pendientes' | 'sin_mesa' | 'presentes' | 'puerta';

const FILTROS: { id: Filtro; texto: string }[] = [
  { id: 'todos', texto: 'Todos' },
  { id: 'registrados', texto: 'Registrados' },
  { id: 'pendientes', texto: 'Pendientes' },
  { id: 'sin_mesa', texto: 'Sin mesa' },
  { id: 'presentes', texto: 'Presentes' },
  { id: 'puerta', texto: 'Alta puerta' },
];

const TOPE = 120;

export function InvitadosScreen() {
  const { eventoId, evento, invitaciones, mesas, usuarios } = useAdminEventoData();
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [abierta, setAbierta] = useState<string | null>(null);
  const [menuAbierto, setMenuAbierto] = useState<string | null>(null);
  const [qrToken, setQrToken] = useState<string | null>(null);

  const nombreAsesor = useMemo(() => {
    const m = new Map(usuarios.map((u) => [u.uid, u.nombre]));
    return (uid: string) => m.get(uid) ?? '—';
  }, [usuarios]);

  const filtradas = useMemo(() => {
    const norm = normalizarNombre(q);
    return invitaciones
      .filter((i) => {
        if (filtro === 'registrados' && i.estado !== 'registrada') return false;
        if (filtro === 'pendientes' && i.estado === 'registrada') return false;
        if (filtro === 'pendientes' && i.estado === 'anulada') return false;
        if (filtro === 'sin_mesa' && (i.mesaId || i.estado !== 'registrada')) return false;
        if (
          filtro === 'presentes' &&
          !i.asistencia?.titular.presente &&
          !i.asistencia?.acompanante.presente
        )
          return false;
        if (filtro === 'puerta' && i.origen !== 'puerta') return false;
        if (norm) {
          // En vivo, no solo `nombreNormalizado` (solo cubre al titular): asi
          // tambien se encuentra por el nombre del acompañante.
          const enTitular = normalizarNombre(i.invitado.nombre || '').includes(norm);
          const enAcompanante = i.invitado.acompanante && normalizarNombre(i.invitado.acompanante).includes(norm);
          const enTel =
            i.telefonoUlt4 && q.replace(/\D/g, '').length >= 2
              ? i.telefonoUlt4.includes(q.replace(/\D/g, ''))
              : false;
          if (!enTitular && !enAcompanante && !enTel) return false;
        }
        return true;
      })
      .sort((a, b) => (a.nombreNormalizado || 'zzz').localeCompare(b.nombreNormalizado || 'zzz'));
  }, [invitaciones, q, filtro]);

  const visibles = filtradas.slice(0, TOPE);

  return (
    <div className="space-y-4">
      <TextField
        label="Buscar"
        type="search"
        placeholder="Nombre o 4 dígitos del teléfono"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />

      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTROS.map((f) => (
          <Chip key={f.id} activo={filtro === f.id} onClick={() => setFiltro(f.id)}>
            {f.texto}
          </Chip>
        ))}
      </div>

      <p className="text-sm text-texto/60">
        {filtradas.length} resultado{filtradas.length === 1 ? '' : 's'}
        {filtradas.length > TOPE && ` · mostrando ${TOPE}, afina la búsqueda`}
      </p>

      <ul className="space-y-2">
        {visibles.map((i) => (
          <li key={i.token}>
            <Card>
              <div className="flex items-center gap-1 p-2 sm:p-2.5">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-xl p-2 text-left"
                  onClick={() => setAbierta(abierta === i.token ? null : i.token)}
                >
                  <span className="flex min-w-0 flex-col gap-1">
                    <strong className="truncate text-sm text-texto">
                      {i.invitado.nombre || <em className="text-texto/40">sin registrar</em>}
                    </strong>
                    {i.invitado.acompanante ? (
                      <span className="truncate text-xs text-texto/50">+ {i.invitado.acompanante}</span>
                    ) : (
                      i.cupo === 2 &&
                      i.estado === 'registrada' && (
                        <span className="truncate text-xs italic text-texto/40">Sin acompañante</span>
                      )
                    )}
                    <EstadoBadge invitacion={i} />
                  </span>
                  <span className="flex shrink-0 flex-col items-end gap-1 text-right">
                    <span className="flex items-center gap-1.5">
                      {i.prioridad && (
                        <span className="rounded-full bg-oro/20 px-2.5 py-0.5 text-xs font-semibold text-navy">
                          {i.prioridad}
                        </span>
                      )}
                      {i.mesaId ? (
                        <span className="rounded-full bg-navy/5 px-2.5 py-0.5 text-xs font-medium text-navy">
                          Mesa {mesas.find((m) => m.id === i.mesaId)?.numero ?? '?'}
                        </span>
                      ) : (
                        <span className="text-xs text-texto/40">sin mesa</span>
                      )}
                    </span>
                    <span className="text-xs text-texto/50">{nombreAsesor(i.asesorUid)}</span>
                  </span>
                </button>
                <MenuAcciones
                  abierto={menuAbierto === i.token}
                  onToggle={() => setMenuAbierto(menuAbierto === i.token ? null : i.token)}
                  onCerrar={() => setMenuAbierto(null)}
                  onVerQR={() => {
                    setQrToken(i.token);
                    setMenuAbierto(null);
                  }}
                  onAnular={() => {
                    setMenuAbierto(null);
                    if (i.estado === 'anulada') return;
                    if (!window.confirm(`¿Anular la invitación de "${i.invitado.nombre || 'este invitado'}"? No se puede deshacer desde aquí.`)) return;
                    void updateDoc(doc(db, 'eventos', eventoId, 'invitaciones', i.token), { estado: 'anulada' });
                  }}
                  anulada={i.estado === 'anulada'}
                />
              </div>
              {abierta === i.token && (
                <Ficha
                  eventoId={eventoId}
                  invitacion={i}
                  mesas={mesas}
                  prioridades={evento?.prioridades ?? []}
                  asesor={nombreAsesor(i.asesorUid)}
                />
              )}
            </Card>
          </li>
        ))}
      </ul>

      {qrToken && (
        <ModalQR token={qrToken} onCerrar={() => setQrToken(null)} />
      )}
    </div>
  );
}

function EstadoBadge({ invitacion }: { invitacion: Invitacion }) {
  const presente = Boolean(invitacion.asistencia?.titular.presente || invitacion.asistencia?.acompanante.presente);
  const { texto, clase } = presente
    ? { texto: 'Presente', clase: 'bg-emerald-50 text-emerald-700' }
    : invitacion.estado === 'anulada'
      ? { texto: 'Anulada', clase: 'bg-texto/5 text-texto/40' }
      : invitacion.estado === 'registrada'
        ? { texto: 'Confirmado', clase: 'bg-sky-50 text-sky-700' }
        : { texto: 'Pendiente', clase: 'bg-oro/15 text-navy/70' };

  return (
    <span className={['inline-flex w-fit rounded-full px-2 py-0.5 text-[11px] font-medium', clase].join(' ')}>
      {texto}
    </span>
  );
}

function MenuAcciones({
  abierto,
  onToggle,
  onCerrar,
  onVerQR,
  onAnular,
  anulada,
}: {
  abierto: boolean;
  onToggle: () => void;
  onCerrar: () => void;
  onVerQR: () => void;
  onAnular: () => void;
  anulada: boolean;
}) {
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        aria-label="Más acciones"
        aria-expanded={abierto}
        className="flex h-9 w-9 items-center justify-center rounded-full text-texto/50 transition hover:bg-navy/5 hover:text-texto active:scale-[0.98]"
        onClick={onToggle}
      >
        <span aria-hidden="true" className="text-lg leading-none">⋮</span>
      </button>
      {abierto && (
        <>
          <button
            type="button"
            aria-label="Cerrar menú"
            className="fixed inset-0 z-30 cursor-default"
            onClick={onCerrar}
          />
          <div className="absolute right-0 top-10 z-40 w-44 overflow-hidden rounded-xl border border-borde bg-white py-1 shadow-lg">
            <button
              type="button"
              className="block w-full px-3.5 py-2.5 text-left text-sm text-texto hover:bg-navy/5"
              onClick={onVerQR}
            >
              Ver QR
            </button>
            <button
              type="button"
              disabled={anulada}
              className="block w-full px-3.5 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-texto/30 disabled:hover:bg-transparent"
              onClick={onAnular}
            >
              {anulada ? 'Ya anulada' : 'Anular invitación'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ModalQR({ token, onCerrar }: { token: string; onCerrar: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCerrar}
    >
      <div
        className="flex flex-col items-center gap-4 rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <QR valor={`${window.location.origin}/i/${token}`} tam={200} />
        <Button variante="secundario" onClick={onCerrar}>
          Cerrar
        </Button>
      </div>
    </div>
  );
}

function Ficha({
  eventoId,
  invitacion,
  mesas,
  prioridades,
  asesor,
}: {
  eventoId: string;
  invitacion: Invitacion;
  mesas: MesaConId[];
  prioridades: string[];
  asesor: string;
}) {
  const [nombre, setNombre] = useState(invitacion.invitado.nombre);
  const [llevaAcompanante, setLlevaAcompanante] = useState(Boolean(invitacion.invitado.acompanante));
  const [acompanante, setAcompanante] = useState(invitacion.invitado.acompanante ?? '');
  const [telefono, setTelefono] = useState(invitacion.invitado.telefono);
  const [notas, setNotas] = useState(invitacion.invitado.notas);
  const [mesaId, setMesaId] = useState(invitacion.mesaId ?? '');
  const [prioridad, setPrioridad] = useState(invitacion.prioridad ?? '');
  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState(false);

  async function guardar() {
    setGuardando(true);
    setOk(false);
    try {
      const conAcompanante = invitacion.cupo === 2 && llevaAcompanante;
      await updateDoc(doc(db, 'eventos', eventoId, 'invitaciones', invitacion.token), {
        'invitado.nombre': nombre,
        'invitado.acompanante': conAcompanante ? acompanante : null,
        'invitado.telefono': telefono,
        'invitado.notas': notas,
        nombreNormalizado: normalizarNombre(nombre),
        telefonoUlt4: telefono.replace(/\D/g, '').slice(-4),
        mesaId: mesaId || null,
        prioridad: prioridad || null,
      });
      setOk(true);
      setTimeout(() => setOk(false), 1600);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-4 border-t border-borde p-4">
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-texto/50">Estado</dt>
        <dd><EstadoInvitacionBadge estado={invitacion.estado} /></dd>
        <dt className="text-texto/50">Cupo</dt>
        <dd className="text-texto">{invitacion.cupo === 2 ? 'Pareja' : 'Individual'}</dd>
        <dt className="text-texto/50">Origen</dt>
        <dd className="text-texto">{invitacion.origen}</dd>
        <dt className="text-texto/50">Asesor</dt>
        <dd className="text-texto">{asesor}</dd>
      </dl>

      <TextField label="Nombre del invitado" value={nombre} onChange={(e) => setNombre(e.target.value)} />

      {invitacion.cupo === 2 && (
        <div className="space-y-2">
          <span className="text-sm font-medium text-texto">¿Lleva acompañante?</span>
          <div className="flex gap-2">
            <Chip activo={llevaAcompanante} onClick={() => setLlevaAcompanante(true)}>
              Sí lleva
            </Chip>
            <Chip
              activo={!llevaAcompanante}
              onClick={() => {
                setLlevaAcompanante(false);
                setAcompanante('');
              }}
            >
              No lleva
            </Chip>
          </div>
          {llevaAcompanante && (
            <TextField
              label="Nombre del acompañante"
              value={acompanante}
              onChange={(e) => setAcompanante(e.target.value)}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-texto" htmlFor={`m-${invitacion.token}`}>
            Mesa
          </label>
          <select
            id={`m-${invitacion.token}`}
            className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
            value={mesaId}
            onChange={(e) => setMesaId(e.target.value)}
          >
            <option value="">Sin mesa</option>
            {mesas.map((m) => (
              <option key={m.id} value={m.id}>
                {m.numero} · {m.nombre} (cap. {m.capacidad})
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-texto" htmlFor={`p-${invitacion.token}`}>
            Prioridad
          </label>
          <select
            id={`p-${invitacion.token}`}
            className="w-full rounded-lg border border-borde bg-white px-3.5 py-2.5 text-sm text-texto focus:border-navy/30 focus:outline-none focus:ring-2 focus:ring-navy/15"
            value={prioridad}
            onChange={(e) => setPrioridad(e.target.value)}
          >
            <option value="">Sin prioridad</option>
            {prioridades.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
      </div>

      <TextField label="Teléfono" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
      <TextField label="Notas" value={notas} onChange={(e) => setNotas(e.target.value)} />

      <Button variante="secundario" bloque cargando={guardando} onClick={guardar}>
        {ok ? 'Guardado' : 'Guardar cambios'}
      </Button>
    </div>
  );
}
